import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, getApiError, TOKEN_KEY } from "@/api/client";

const AuthContext = createContext(null);
const ADMIN_ROLES = new Set(["moderator", "super_admin"]);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading");
  const [authError, setAuthError] = useState("");

  const applyUser = useCallback((nextUser) => {
    setUser(nextUser);
    setStatus(ADMIN_ROLES.has(nextUser?.role) ? "authorized" : "unauthorized");
  }, []);

  const fetchMe = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    applyUser(data?.user || data);
  }, [applyUser]);

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      setStatus("signed-out");
      return;
    }
    fetchMe().catch(() => {
      localStorage.removeItem(TOKEN_KEY);
      setStatus("signed-out");
    });
  }, [fetchMe]);

  const signInWithGoogle = useCallback(async (credential) => {
    setStatus("loading");
    setAuthError("");
    try {
      const { data } = await api.post("/auth/google", { id_token: credential });
      const token = data?.session_token || data?.token || data?.data?.session_token;
      if (!token) throw new Error("The authentication response did not include a session token.");
      localStorage.setItem(TOKEN_KEY, token);
      await fetchMe();
    } catch (error) {
      localStorage.removeItem(TOKEN_KEY);
      setAuthError(getApiError(error));
      setStatus("signed-out");
    }
  }, [fetchMe]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
      setStatus("signed-out");
    }
  }, []);

  const value = useMemo(() => ({ user, status, authError, signInWithGoogle, logout }), [user, status, authError, signInWithGoogle, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);